import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'

import Alert from '@material-ui/lab/Alert'
import { Spacer } from '@vestaboard/installables'
import { useAuth0 } from '@auth0/auth0-react'
import React from 'react'
import Helmet from 'react-helmet'
import { useHistory } from 'react-router'
import { useGetPersonQuery } from '../../../api/useGetPersonName'
import { useUnpin } from '../../../api/useUnpin'
import { ApplyActivationCode } from '../../board/components/ApplyActivationCode'
import { InternalSwitch } from '../../board/components/InternalSwitch'
import { useAddPlusSubscription } from '../../board/hooks/useAddPlusSubscription'
import { useBoardDetailQuery } from '../../board/hooks/useBoardDetailQuery'
import { useExpirePlusSubscription } from '../../board/hooks/useExpirePlusSubscription'
import { FormattedDateTime } from '../../formatted-date-time'
import { Link } from '../../link'
import { Channels } from '../../shared/Channels'
import { TruncatedId } from '../../truncated-id'
import { useCreateDisplayGroupRole } from '../hooks/useCreateDisplayGroupRole'
import { useDeleteDisplayGroupInvite } from '../hooks/useDeleteDisplayGroupInvite'
import { useDeleteDisplayGroupRole } from '../hooks/useDeleteDisplayGroupRole'
import { useGetDisplayGroup } from '../hooks/useGetDisplayGroup'
import { useUpdateDisplayGroup } from '../hooks/useUpdateDisplayGroup'
import { useUpdateDisplayGroupRole } from '../hooks/useUpdateDisplayGroupRole'

enum DisplayGroupPermission {
  Admin = 'Admin',
  Owner = 'Owner',
  User = 'User'
}

enum NoteArrayBoardStyle {
  NoteGroup = 'note-group',
  NoteGroupWhite = 'note-group-white'
}

interface IDisplayGroupDetailProps {
  displayGroupId: string
}

const useStyles = makeStyles({
  title: {
    paddingBottom: 14
  },
  sectionTitle: {
    paddingTop: 32,
    paddingBottom: 14
  },
  card: {
    padding: 16,
    marginBottom: 16
  },
  gridCell: {
    border: '1px solid #ccc',
    padding: 8,
    textAlign: 'center',
    minWidth: 100
  }
})

export const DisplayGroupDetail = (props: IDisplayGroupDetailProps) => {
  const classes = useStyles()
  const history = useHistory()
  const [confirmAddPlus, setConfirmAddPlus] = React.useState(false)
  const [confirmExpire, setConfirmExpire] = React.useState(false)
  const [expiring, setExpiring] = React.useState(false)
  const [isUnpinning, setIsUnpinning] = React.useState(false)
  const [roleChanges, setRoleChanges] = React.useState<{ [key: string]: string }>({})
  const [showAddMember, setShowAddMember] = React.useState(false)
  const [newMemberEmail, setNewMemberEmail] = React.useState('')
  const [newMemberRole, setNewMemberRole] = React.useState<DisplayGroupPermission>(DisplayGroupPermission.User)
  const [addingMember, setAddingMember] = React.useState(false)
  const [hawkbitDialogOpen, setHawkbitDialogOpen] = React.useState(false)
  const [hawkbitUpgrading, setHawkbitUpgrading] = React.useState(false)
  const [hawkbitResults, setHawkbitResults] = React.useState<Map<string, string>>(new Map())
  const hawkbitAbortRef = React.useRef<AbortController | null>(null)
  const { getAccessTokenSilently } = useAuth0()
  const {
    data,
    loading: displayGroupLoading,
    error: displayGroupError,
    refetch: refetchDisplayGroup
  } = useGetDisplayGroup(props.displayGroupId)
  const { data: boardData, loading: boardLoading, error: boardError, refetch } = useBoardDetailQuery(
    props.displayGroupId
  )
  const [expirePlusSubscription] = useExpirePlusSubscription()
  const [addPlusSubscription] = useAddPlusSubscription()
  const [unpinMutation, { loading: unpinning }] = useUnpin()
  const [createDisplayGroupRole] = useCreateDisplayGroupRole()
  const [updateDisplayGroupRole] = useUpdateDisplayGroupRole()
  const [updateDisplayGroup, { loading: updatingBoardStyle }] = useUpdateDisplayGroup()
  const [deleteDisplayGroupRole] = useDeleteDisplayGroupRole()
  const [deleteDisplayGroupInvite] = useDeleteDisplayGroupInvite()
  const loading = displayGroupLoading || boardLoading
  const error = displayGroupError || boardError

  const displayGroup = data?.getDisplayGroup?.__typename === 'DisplayGroupV2' ? data.getDisplayGroup : null

  const { data: ownerData, loading: ownerLoading } = useGetPersonQuery({
    skip: !displayGroup?.ownerId,
    variables: { personId: displayGroup?.ownerId || '' }
  })

  if (loading) {
    return <CircularProgress />
  }

  if (error || !displayGroup) {
    const errorMessage =
      data?.getDisplayGroup?.__typename === 'DisplayGroupErrorV2' ? data.getDisplayGroup.error : 'Note Array not found.'
    return <Alert severity='error'>{errorMessage}</Alert>
  }

  const ownerName = ownerData?.person
    ? `${ownerData.person.firstName} ${ownerData.person.lastName}`
    : displayGroup.ownerId

  const doExpire = async () => {
    setExpiring(true)
    await expirePlusSubscription({ variables: { boardId: props.displayGroupId } })
    await refetch()
    setExpiring(false)
    setConfirmExpire(false)
  }

  const doAddPlus = async () => {
    setExpiring(true)
    await addPlusSubscription({ variables: { boardId: props.displayGroupId } })
    await refetch()
    setExpiring(false)
    setConfirmAddPlus(false)
  }

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) return

    setAddingMember(true)
    try {
      await createDisplayGroupRole({
        variables: {
          input: {
            groupId: props.displayGroupId,
            email: newMemberEmail.trim(),
            role: newMemberRole
          }
        }
      })

      await refetchDisplayGroup()
      setShowAddMember(false)
      setNewMemberEmail('')
      setNewMemberRole(DisplayGroupPermission.User)
    } catch (error) {
      console.error('Error adding member:', error)
    } finally {
      setAddingMember(false)
    }
  }

  const allDevices = displayGroup.boards
    .flat()
    .filter(b => b?.board?.pairedDevice)
    .map(b => ({
      boardId: b.id,
      deviceId: b.board.pairedDevice!.id,
      firmwareVersion: b.board.pairedDevice!.firmwareVersion || '',
      pairingCode: b.board.pairedDevice!.pairingCode || ''
    }))

  const handleHawkbitUpgrade = async () => {
    const abort = new AbortController()
    hawkbitAbortRef.current = abort
    setHawkbitUpgrading(true)
    setHawkbitResults(new Map())
    try {
      const token = await getAccessTokenSilently()
      for (const device of allDevices) {
        if (abort.signal.aborted) break
        if (!device.pairingCode) {
          setHawkbitResults(prev => new Map(prev).set(device.deviceId, 'skipped - no pairing code'))
          continue
        }
        try {
          await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(resolve, 2000)
            abort.signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')) })
          })
          const response = await fetch(`https://platform.vestaboard.com/v3.0/force-upgrade/${device.deviceId}`, {
            method: 'POST',
            headers: { 'X-Vestaboard-Pairing-Code': device.pairingCode, 'X-Vestaboard-Token': token },
            signal: abort.signal
          })
          setHawkbitResults(prev =>
            new Map(prev).set(device.deviceId, response.ok ? 'success' : `error-${response.status}`)
          )
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') break
          setHawkbitResults(prev => new Map(prev).set(device.deviceId, 'error'))
        }
      }
    } finally {
      hawkbitAbortRef.current = null
      setHawkbitUpgrading(false)
    }
  }

  const handleHawkbitCancel = () => {
    hawkbitAbortRef.current?.abort()
  }

  const boardV2 = boardData?.getBoardV2?.__typename === 'BoardV2' ? boardData.getBoardV2 : null

  const plusProductApplication = boardData?.board?.productApplications
    ?.filter(pa => pa.pricing?.product?.isPlus)
    ?.sort((a, b) => (a.ends > b.ends ? -1 : a.ends < b.ends ? 1 : 0))[0]

  return (
    <>
      <Dialog maxWidth='sm' open={showAddMember} onClose={() => setShowAddMember(false)}>
        <DialogTitle>Add Member or Invite</DialogTitle>
        <DialogContent dividers>
          <Box>
            <TextField
              fullWidth
              label='Email Address'
              value={newMemberEmail}
              onChange={e => setNewMemberEmail(e.target.value)}
              placeholder='user@example.com'
              margin='normal'
              type='email'
            />
            <Box mt={2}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Role
              </Typography>
              <Select
                fullWidth
                value={newMemberRole}
                onChange={e => setNewMemberRole(e.target.value as DisplayGroupPermission)}>
                <MenuItem value={DisplayGroupPermission.Owner}>Owner</MenuItem>
                <MenuItem value={DisplayGroupPermission.Admin}>Admin</MenuItem>
                <MenuItem value={DisplayGroupPermission.User}>User</MenuItem>
              </Select>
            </Box>
            <Box mt={2}>
              <Typography variant='caption' color='textSecondary'>
                If the person exists in the system, they will be added as a member. Otherwise, an invite will be sent.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddMember(false)} disabled={addingMember}>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={handleAddMember}
            disabled={addingMember || !newMemberEmail.trim()}>
            {addingMember ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmExpire} onClose={() => setConfirmExpire(false)}>
        <DialogTitle>Remove Plus subscription from board</DialogTitle>
        <DialogContent dividers>
          Are you sure you would like to remove the Plus subscription for this board?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAddPlus(false)}>{expiring ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button color='primary' variant='outlined' onClick={() => doExpire()}>
            {expiring ? <CircularProgress /> : 'Yes, Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmAddPlus} onClose={() => setConfirmAddPlus(false)}>
        <DialogTitle>Add Plus subscription to board</DialogTitle>
        <DialogContent dividers>Are you sure you would like to add a Plus subscription to this board?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExpire(false)}>{expiring ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button color='primary' variant='outlined' onClick={() => doAddPlus()}>
            {expiring ? <CircularProgress /> : 'Yes, Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={isUnpinning} onClose={() => setIsUnpinning(false)}>
        <DialogTitle>Unpin Message</DialogTitle>
        <DialogContent dividers>Are you sure you want to unpin the message from this board?</DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUnpinning(false)}>{unpinning ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button
            color='primary'
            variant='outlined'
            onClick={async () => {
              await unpinMutation({
                variables: {
                  input: {
                    boardId: props.displayGroupId
                  }
                }
              })
              await new Promise(resolve => {
                setTimeout(async () => {
                  await refetch()
                  resolve(true)
                }, 2500)
              })
              setIsUnpinning(false)
            }}>
            {unpinning ? <CircularProgress /> : 'Yes, Unpin'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box>
        <Helmet>
          <title>Note Array: {displayGroup.title || 'Untitled'}</title>
        </Helmet>

        <Typography variant='button' display='block' gutterBottom>
          <Link to='/note-arrays'>Note Array</Link>
        </Typography>
        <Typography variant='h4' className={classes.title}>
          {displayGroup.title || 'Untitled'}
        </Typography>

        <Card className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Group ID
              </Typography>
              <Typography variant='body1'>{displayGroup.id}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Title
              </Typography>
              <Typography variant='body1'>{displayGroup.title || 'Untitled'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Board Style
              </Typography>
              <Select
                value={displayGroup.boardStyle || NoteArrayBoardStyle.NoteGroup}
                disabled={updatingBoardStyle}
                onChange={async e => {
                  await updateDisplayGroup({
                    variables: {
                      input: {
                        id: props.displayGroupId,
                        title: displayGroup.title,
                        boardStyle: e.target.value as NoteArrayBoardStyle,
                        boardIds: displayGroup.boards.map(row => row.map(board => board.id))
                      }
                    }
                  })
                  await refetchDisplayGroup()
                }}>
                <MenuItem value={NoteArrayBoardStyle.NoteGroup}>Black</MenuItem>
                <MenuItem value={NoteArrayBoardStyle.NoteGroupWhite}>White</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Owner
              </Typography>
              <Typography variant='body1'>
                {ownerLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Link to={`/person/${displayGroup.ownerId}`}>{ownerName}</Link>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Dimensions
              </Typography>
              <Typography variant='body1'>
                {displayGroup.rows} rows × {displayGroup.columns} columns
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Created
              </Typography>
              <Typography variant='body1'>
                <FormattedDateTime value={displayGroup.createdAt} />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Updated
              </Typography>
              <Typography variant='body1'>
                {displayGroup.updatedAt ? <FormattedDateTime value={displayGroup.updatedAt} /> : 'Never'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Internal Board
              </Typography>
              <Typography variant='body1'>
                <InternalSwitch boardId={props.displayGroupId} internalBoard={!!boardData?.board?.internalBoard} />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Is Plus
              </Typography>
              <Typography variant='body1'>
                <Switch
                  onChange={e => {
                    if (e.target.checked === false) {
                      setConfirmExpire(true)
                    } else {
                      setConfirmAddPlus(true)
                    }
                  }}
                  checked={!!plusProductApplication}
                />
              </Typography>
            </Grid>
            {!!plusProductApplication ? (
              <>
                <Grid item xs={12} md={6}>
                  <Typography variant='body2' color='textSecondary'>
                    Plus Subscription ID
                  </Typography>
                  <Typography variant='body1'>
                    <TruncatedId value={plusProductApplication.id} path={`/plus/${plusProductApplication.id}`} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='body2' color='textSecondary'>
                    Plus activation date
                  </Typography>
                  <Typography variant='body1'>
                    <FormattedDateTime value={plusProductApplication.started} hideTime />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='body2' color='textSecondary'>
                    Plus ending date
                  </Typography>
                  <Typography variant='body1'>
                    <FormattedDateTime value={plusProductApplication.ends} hideTime />
                  </Typography>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='textSecondary'>
                  Plus Redemption Code
                </Typography>
                <Typography variant='body1'>
                  <ApplyActivationCode boardId={props.displayGroupId} />
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Pinned message until
              </Typography>
              <Typography variant='body1'>
                {boardV2?.pinnedMessage ? (
                  <Box>
                    <FormattedDateTime value={boardV2.pinnedMessage.pinUntil}></FormattedDateTime>
                    <Spacer size='small' />
                    <Button
                      variant='outlined'
                      onClick={() => {
                        setIsUnpinning(true)
                      }}>
                      Unpin
                    </Button>
                  </Box>
                ) : (
                  'None'
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Web app link
              </Typography>
              <Typography variant='body1'>
                <Button
                  variant='outlined'
                  onClick={() => {
                    window.open(`https://web.vestaboard.com/board/group-${props.displayGroupId}/settings/`)
                  }}>
                  Use board in web app
                </Button>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Live view
              </Typography>
              <Typography variant='body1'>
                <Button variant='outlined' onClick={() => history.push(`/note-arrays/${props.displayGroupId}/live`)}>
                  Live View
                </Button>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Message appearance history
              </Typography>
              <Typography variant='body1'>
                <Button
                  variant='outlined'
                  onClick={() => history.push(`/note-arrays/${props.displayGroupId}/message-appearance-history`)}>
                  View History
                </Button>
              </Typography>
            </Grid>
          </Grid>
        </Card>

        <Dialog maxWidth='md' open={hawkbitDialogOpen} onClose={() => !hawkbitUpgrading && setHawkbitDialogOpen(false)}>
          <DialogTitle>Upgrade All Devices via HawkBit</DialogTitle>
          <DialogContent dividers>
            <Typography variant='body2' gutterBottom>
              This will queue a firmware upgrade to v4.5.4 for {allDevices.length} device(s) in this array.
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Current Firmware</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allDevices.map(device => {
                  const result = hawkbitResults.get(device.deviceId)
                  return (
                    <TableRow key={device.deviceId}>
                      <TableCell><TruncatedId value={device.deviceId} /></TableCell>
                      <TableCell>{device.firmwareVersion || 'Unknown'}</TableCell>
                      <TableCell>
                        {result === 'success' ? '✓ Queued' : result ? `✗ ${result}` : hawkbitUpgrading ? '...' : '—'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            {hawkbitUpgrading ? (
              <Button color='secondary' variant='contained' onClick={handleHawkbitCancel}>
                Stop
              </Button>
            ) : (
              <>
                <Button onClick={() => setHawkbitDialogOpen(false)}>
                  {hawkbitResults.size > 0 ? 'Done' : 'Cancel'}
                </Button>
                {hawkbitResults.size === 0 && (
                  <Button color='primary' variant='contained' onClick={handleHawkbitUpgrade}>
                    Upgrade All
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

        <Box display='flex' justifyContent='space-between' alignItems='center' className={classes.sectionTitle}>
          <Typography variant='h5'>Board Grid ({displayGroup.boards.flat().length} boards)</Typography>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setHawkbitDialogOpen(true)}
            disabled={allDevices.length === 0}>
            Upgrade All via HawkBit
          </Button>
        </Box>
        <Card className={classes.card}>
          <Table>
            <TableBody>
              {displayGroup.boards.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((board, colIndex) => (
                    <TableCell key={`${rowIndex}-${colIndex}`} className={classes.gridCell}>
                      {board ? (
                        <>
                          <Button variant='outlined' size='small' onClick={() => history.push(`/boards/${board.id}`)}>
                            <TruncatedId value={board.id} />
                          </Button>
                          <Typography variant='caption' display='block'>
                            {board?.board?.title}
                          </Typography>
                          {board?.board?.pairedDevice?.firmwareVersion && (
                            <Typography variant='caption' display='block' color='textSecondary'>
                              {board.board.pairedDevice.firmwareVersion}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography color='textSecondary'>Empty</Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Box display='flex' justifyContent='space-between' alignItems='center' className={classes.sectionTitle}>
          <Typography variant='h5'>Members ({displayGroup.roles.length})</Typography>
          <Button variant='contained' color='primary' onClick={() => setShowAddMember(true)}>
            Add Member
          </Button>
        </Box>
        <Card className={classes.card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Person ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Added</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayGroup.roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color='textSecondary'>No members</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayGroup.roles.map(role => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Button variant='outlined' size='small' onClick={() => history.push(`/person/${role.personId}`)}>
                        <TruncatedId value={role.personId} />
                      </Button>
                    </TableCell>
                    <TableCell>{role.person ? `${role.person.firstName} ${role.person.lastName}` : 'N/A'}</TableCell>
                    <TableCell>{role.person?.emailAddress || 'N/A'}</TableCell>
                    <TableCell>
                      <Select
                        value={roleChanges[role.id] ?? role.role}
                        onChange={async e => {
                          const newRole = e.target.value as DisplayGroupPermission
                          setRoleChanges({
                            ...roleChanges,
                            [role.id]: newRole
                          })
                          await updateDisplayGroupRole({
                            variables: {
                              input: {
                                groupId: props.displayGroupId,
                                personId: role.personId,
                                role: newRole
                              }
                            }
                          })
                          await refetchDisplayGroup()
                        }}>
                        <MenuItem value={'Owner'}>Owner</MenuItem>
                        <MenuItem value={'Admin'}>Admin</MenuItem>
                        <MenuItem value={'User'}>User</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <FormattedDateTime value={role.createdAt} />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        size='small'
                        color='secondary'
                        onClick={async () => {
                          if (
                            window.confirm(
                              `Remove ${role.person?.firstName} ${role.person?.lastName} from this Note Array?`
                            )
                          ) {
                            await deleteDisplayGroupRole({
                              variables: {
                                input: {
                                  groupId: props.displayGroupId,
                                  personId: role.personId
                                }
                              }
                            })
                            await refetchDisplayGroup()
                          }
                        }}>
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        <Typography variant='h5' className={classes.sectionTitle}>
          Pending Invites ({displayGroup.invites.length})
        </Typography>
        <Card className={classes.card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invited</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayGroup.invites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color='textSecondary'>No pending invites</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayGroup.invites.map(invite => (
                  <TableRow key={invite.id}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>{invite.role}</TableCell>
                    <TableCell>
                      <FormattedDateTime value={invite.createdAt} />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        size='small'
                        color='secondary'
                        onClick={async () => {
                          if (window.confirm(`Delete invite for ${invite.email}?`)) {
                            await deleteDisplayGroupInvite({
                              variables: {
                                input: {
                                  groupId: props.displayGroupId,
                                  email: invite.email
                                }
                              }
                            })
                            await refetchDisplayGroup()
                          }
                        }}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
        <Typography variant='h5' className={classes.sectionTitle}>
          V2 Outgoing Webhooks
        </Typography>
        <Card className={classes.card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Installable ID</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Webhook Token</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayGroup.outgoingWebhooks.map((webhook) => (
                <TableRow key={webhook.id}>
                  <TableCell>
                    {webhook.installableId ? (
                      <Link to={`/installables/${webhook.installableId}`}>
                        <TruncatedId full value={webhook.installableId} />
                      </Link>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>{webhook.url}</TableCell>
                  <TableCell>{webhook.token}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {displayGroup.outgoingWebhooks.length === 0 && (
            <Alert severity='info'>There are no v2 outgoing webhooks for this group.</Alert>
          )}
        </Card>

        <Channels
          subscriptions={boardData?.board?.subscriptionSet?.subscriptions}
          className={classes.sectionTitle}
          group
        />
      </Box>
    </>
  )
}
